import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getShippingCompanies, createShippingCompany, updateShippingCompany, deleteShippingCompany, listShippingLines, createShippingLine, deleteShippingLine } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import MoneyInput from '../../components/MoneyInput';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, MagnifyingGlass, Pencil, Trash, Truck, Star, MapPin, Clock, CurrencyDollar, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ShippingPage = () => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '', contact_email: '', contact_phone: '', shipping_route: '',
    estimated_days: '', price_per_unit: '', tracking_available: false,
    reliability_rating: '', notes: '', is_active: true
  });

  // Shipping Lines (SCAC) state
  const [shippingLines, setShippingLines] = useState({ built_in: [], custom: [] });
  const [scacDialogOpen, setScacDialogOpen] = useState(false);
  const [scacForm, setScacForm] = useState({ scac: '', name: '' });

  const loadShippingLines = useCallback(async () => {
    try {
      const res = await listShippingLines();
      setShippingLines(res.data || { built_in: [], custom: [] });
    } catch (err) { console.error('Failed to load shipping lines', err); }
  }, []);

  const handleAddScac = async (e) => {
    e.preventDefault();
    try {
      await createShippingLine({ scac: scacForm.scac, name: scacForm.name });
      toast.success('Shipping line added');
      setScacDialogOpen(false);
      setScacForm({ scac: '', name: '' });
      loadShippingLines();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add shipping line');
    }
  };

  const handleDeleteScac = async (line) => {
    if (!window.confirm(`Remove shipping line "${line.name}" (${line.scac})?`)) return;
    try {
      await deleteShippingLine(line.id);
      toast.success('Shipping line removed');
      loadShippingLines();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const loadCompanies = useCallback(async () => {
    try {
      const res = await getShippingCompanies({ search, sort_by: sortBy === 'default' ? undefined : sortBy });
      setCompanies(res.data);
    } catch {
      toast.error('Failed to load shipping companies');
    } finally {
      setLoading(false);
    }
  }, [search, sortBy]);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);
  useEffect(() => { loadShippingLines(); }, [loadShippingLines]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        estimated_days: formData.estimated_days ? parseInt(formData.estimated_days) : null,
        price_per_unit: formData.price_per_unit ? parseFloat(formData.price_per_unit) : null,
        reliability_rating: formData.reliability_rating ? parseInt(formData.reliability_rating) : null
      };
      if (selectedCompany) {
        await updateShippingCompany(selectedCompany.id, data);
        toast.success('Shipping company updated');
      } else {
        await createShippingCompany(data);
        toast.success('Shipping company created');
      }
      setDialogOpen(false);
      resetForm();
      loadCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (company) => {
    if (!window.confirm(`Delete "${company.name}"?`)) return;
    try {
      await deleteShippingCompany(company.id);
      toast.success('Shipping company deleted');
      loadCompanies();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const openEdit = (company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name || '',
      contact_email: company.contact_email || '',
      contact_phone: company.contact_phone || '',
      shipping_route: company.shipping_route || '',
      estimated_days: company.estimated_days?.toString() || '',
      price_per_unit: company.price_per_unit?.toString() || '',
      tracking_available: company.tracking_available ?? false,
      reliability_rating: company.reliability_rating?.toString() || '',
      notes: company.notes || '',
      is_active: company.is_active ?? true
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedCompany(null);
    setFormData({
      name: '', contact_email: '', contact_phone: '', shipping_route: '',
      estimated_days: '', price_per_unit: '', tracking_available: false,
      reliability_rating: '', notes: '', is_active: true
    });
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star key={`star-${i}`} weight={i < rating ? 'fill' : 'regular'} className={`w-4 h-4 ${i < rating ? 'text-amber-500' : 'text-gray-300'}`} />
    ));
  };

  return (
    <div data-testid="shipping-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('shipping.title')}</h1>
          <p className="text-muted-foreground text-sm">Manage shipping partners and compare options</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-shipping-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('shipping.addCompany')}
        </Button>
      </div>

      {/* Shipping Lines (SCAC codes) — used for container tracking */}
      <div className="mb-6 bg-white border border-border" data-testid="shipping-lines-section">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-slate-50">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider">Shipping Lines (for container tracking)</h3>
            <p className="text-[11px] text-muted-foreground">Used in the "Shipping Line" dropdown when starting container tracking. Built-in lines cover all major carriers; add custom ones if needed.</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => { setScacForm({ scac: '', name: '' }); setScacDialogOpen(true); }} data-testid="add-shipping-line-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Custom
          </Button>
        </div>
        <div className="p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {shippingLines.built_in?.map(s => (
            <div key={s.scac} className="px-3 py-2 border border-border bg-slate-50" data-testid={`builtin-line-${s.scac}`}>
              <p className="text-sm font-medium">{s.name}</p>
              <p className="text-[10px] font-mono text-muted-foreground">{s.scac} · built-in</p>
            </div>
          ))}
          {shippingLines.custom?.map(s => (
            <div key={s.id} className="px-3 py-2 border-2 border-primary bg-primary/5 group relative" data-testid={`custom-line-${s.scac}`}>
              <p className="text-sm font-medium">{s.name}</p>
              <p className="text-[10px] font-mono text-muted-foreground">{s.scac} · custom</p>
              <button
                onClick={() => handleDeleteScac(s)}
                className="absolute top-1 right-1 w-5 h-5 bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`delete-line-${s.scac}`}
                title="Remove"
              >
                <Trash className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-brutalist pl-10"
            data-testid="shipping-search"
          />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-48 input-brutalist">
            <SelectValue placeholder="Sort by..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Default</SelectItem>
            <SelectItem value="price">Price (Low to High)</SelectItem>
            <SelectItem value="speed">Speed (Fastest)</SelectItem>
            <SelectItem value="reliability">Reliability (Best)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Companies Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : companies.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No shipping companies yet. Add your first shipping partner!
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((company, idx) => (
            <div key={company.id} className={`card-brutalist animate-fade-in stagger-${(idx % 5) + 1}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-secondary flex items-center justify-center">
                    <Truck weight="duotone" className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold">{company.name}</h3>
                    <span className={`text-xs px-2 py-0.5 ${company.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {company.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(company)} data-testid={`edit-shipping-${company.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(company)} className="text-destructive" data-testid={`delete-shipping-${company.id}`}>
                    <Trash className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {company.shipping_route && (
                <div className="flex items-center gap-2 text-sm mb-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span>{company.shipping_route}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-border">
                <div>
                  <div className="flex items-center gap-1 text-muted-foreground text-xs mb-1">
                    <Clock className="w-3 h-3" />
                    Est. Days
                  </div>
                  <p className="font-bold">{company.estimated_days || '-'} days</p>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-muted-foreground text-xs mb-1">
                    <CurrencyDollar className="w-3 h-3" />
                    Price/Unit
                  </div>
                  <p className="font-bold">${company.price_per_unit?.toLocaleString() || '-'}</p>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-1">
                  {renderStars(company.reliability_rating || 0)}
                </div>
                {company.tracking_available && (
                  <div className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle weight="fill" className="w-4 h-4" />
                    Tracking
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedCompany ? t('shipping.editCompany') : t('shipping.addCompany')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('shipping.name')} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="input-brutalist"
                data-testid="shipping-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className="input-brutalist"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Phone</Label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  className="input-brutalist"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('shipping.route')}</Label>
              <Input
                value={formData.shipping_route}
                onChange={(e) => setFormData({ ...formData, shipping_route: e.target.value })}
                placeholder="e.g., Shanghai → Algiers"
                className="input-brutalist"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('shipping.estimatedDays')}</Label>
                <Input
                  type="number"
                  value={formData.estimated_days}
                  onChange={(e) => setFormData({ ...formData, estimated_days: e.target.value })}
                  placeholder="30"
                  className="input-brutalist"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('shipping.pricePerUnit')}</Label>
                <MoneyInput
                  value={formData.price_per_unit}
                  onChange={(v) => setFormData({ ...formData, price_per_unit: v })}
                  placeholder="1 500"
                  className="input-brutalist"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('shipping.reliability')} (1-5)</Label>
                <Select value={formData.reliability_rating} onValueChange={(v) => setFormData({ ...formData, reliability_rating: v })}>
                  <SelectTrigger className="input-brutalist">
                    <SelectValue placeholder="Select rating" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((r) => (
                      <SelectItem key={r} value={r.toString()}>{r} Star{r > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('shipping.trackingAvailable')}</Label>
                <div className="flex items-center h-10">
                  <Switch
                    checked={formData.tracking_available}
                    onCheckedChange={(checked) => setFormData({ ...formData, tracking_available: checked })}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="input-brutalist"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" className="btn-primary" data-testid="shipping-submit-btn">
                {t('common.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Shipping Line (SCAC) Dialog */}
      <Dialog open={scacDialogOpen} onOpenChange={setScacDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Shipping Line</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddScac} className="space-y-4">
            <div className="space-y-2">
              <Label>SCAC Code *</Label>
              <Input
                value={scacForm.scac}
                onChange={(e) => setScacForm({ ...scacForm, scac: e.target.value.toUpperCase() })}
                placeholder="e.g. WHLU"
                maxLength={6}
                required
                className="input-brutalist font-mono uppercase"
                data-testid="scac-code-input"
              />
              <p className="text-[11px] text-muted-foreground">4-letter Standard Carrier Alpha Code. Terminal49 uses this to track containers. Look it up on the carrier's website or terminal49.com/scacs.</p>
            </div>
            <div className="space-y-2">
              <Label>Display Name *</Label>
              <Input
                value={scacForm.name}
                onChange={(e) => setScacForm({ ...scacForm, name: e.target.value })}
                placeholder="e.g. Wan Hai Lines"
                required
                className="input-brutalist"
                data-testid="scac-name-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setScacDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" data-testid="scac-submit-btn">Add</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShippingPage;
